/**
 * SQLite Manager for Web Demo
 *
 * Manages the sql.js SQLite instance for the browser-based demo.
 * Handles initialization, database opening, and resource cleanup.
 */

import initSqlJs, { type Database, type SqlJsStatic } from 'sql.js';

/** Singleton instance of sql.js */
let sqlJsInstance: SqlJsStatic | null = null;

/** Currently open database instance */
let currentDatabase: Database | null = null;

/** ID of the currently connected database (for tracking) */
let currentDatabaseId: string | null = null;

/**
 * Initialize sql.js if not already initialized.
 * Uses the CDN-hosted WASM file for simplicity.
 */
export async function initializeSqlJs(): Promise<SqlJsStatic> {
  if (sqlJsInstance) {
    return sqlJsInstance;
  }

  try {
    // Load sql.js with the WASM file from CDN
    // This avoids needing to bundle the WASM file
    sqlJsInstance = await initSqlJs({
      // Use the CDN URL for the WASM binary
      locateFile: (file: string) => `https://sql.js.org/dist/${file}`,
    });
    console.log('[SQLite] sql.js initialized successfully');
    return sqlJsInstance;
  } catch (error) {
    console.error('[SQLite] Failed to initialize sql.js:', error);
    throw new Error(
      `Failed to initialize SQLite runtime: ${error instanceof Error ? error.message : 'Unknown error'}`
    );
  }
}

/**
 * Open an in-memory SQLite database.
 * Creates a new empty database.
 */
export async function openInMemoryDatabase(databaseId: string): Promise<Database> {
  const sql = await initializeSqlJs();

  // Close any existing database
  closeCurrentDatabase();

  // Create new empty database
  currentDatabase = new sql.Database();
  currentDatabaseId = databaseId;

  console.log(`[SQLite] Opened in-memory database: ${databaseId}`);
  return currentDatabase;
}

/**
 * Open a SQLite database from an ArrayBuffer.
 * Used for loading demo databases or uploaded files.
 */
export async function openDatabaseFromBuffer(
  databaseId: string,
  buffer: ArrayBuffer
): Promise<Database> {
  const sql = await initializeSqlJs();

  // Close any existing database
  closeCurrentDatabase();

  // Create database from the buffer
  const uint8Array = new Uint8Array(buffer);
  currentDatabase = new sql.Database(uint8Array);
  currentDatabaseId = databaseId;

  console.log(`[SQLite] Opened database from buffer: ${databaseId}`);
  return currentDatabase;
}

/**
 * Get the currently open database.
 * Returns null if no database is open.
 */
export function getCurrentDatabase(): Database | null {
  return currentDatabase;
}

/**
 * Get the ID of the currently connected database.
 */
export function getCurrentDatabaseId(): string | null {
  return currentDatabaseId;
}

/**
 * Check if a database is currently open.
 */
export function isDatabaseOpen(): boolean {
  return currentDatabase !== null;
}

/**
 * Close the current database and release resources.
 */
export function closeCurrentDatabase(): void {
  if (currentDatabase) {
    try {
      currentDatabase.close();
      console.log(`[SQLite] Closed database: ${currentDatabaseId}`);
    } catch (error) {
      console.warn('[SQLite] Error closing database:', error);
    }
    currentDatabase = null;
    currentDatabaseId = null;
  }
}

/**
 * Export the current database to an ArrayBuffer.
 * Used for downloading or persisting the database.
 */
export function exportCurrentDatabase(): Uint8Array | null {
  if (!currentDatabase) {
    return null;
  }

  try {
    return currentDatabase.export();
  } catch (error) {
    console.error('[SQLite] Failed to export database:', error);
    return null;
  }
}
